'use client';

import { useEffect, useRef, useState } from 'react';
import { useSupabaseClient, useSession } from '@supabase/auth-helpers-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { startGoogleLogin } from '@/lib/oauth';
import { safeLocalRedirect, syncAulaAccount } from '@/lib/firstRunAuth';
import TurnstileWidget, {
  type TurnstileWidgetHandle,
} from '@/components/auth/TurnstileWidget';
import { Alert, Button, Input } from '@/components/ui';

type Role = 'student' | 'teacher';

type RegistrationCodeCheck = {
  ok: boolean;
  message: string;
};

function normalizeRegistrationCode(value: unknown) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '');
}

function registrationCodeErrorMessage(error: unknown) {
  switch (String(error || '')) {
    case 'missing_code':
    case 'invalid_code':
      return 'That registration code is not valid.';
    case 'inactive_code':
      return 'That registration code is not active.';
    case 'not_valid_for_registration':
      return 'That code cannot be used for a new registration.';
    case 'not_started':
      return 'That registration code is not active yet.';
    case 'expired':
      return 'That registration code has expired.';
    case 'maxed_out':
      return 'That registration code has reached its usage limit.';
    case 'affiliate_inactive':
      return 'That registration code is no longer available.';
    default:
      return 'We could not verify that registration code.';
  }
}

function roleHomeRoute(r: Role) {
  return r === 'teacher' ? '/teacher' : '/student';
}

export default function LoginForm({ role }: { role: Role }) {
  const supabase = useSupabaseClient();
  const session = useSession();
  const router = useRouter();
  const params = useSearchParams();
  const nextOverride = params.get('next') || undefined;
  const incomingMode = String(params.get('mode') || '').trim().toLowerCase();
  const incomingCode = normalizeRegistrationCode(params.get('code'));

  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [selectedRole, setSelectedRole] = useState<Role>(role);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [signupSent, setSignupSent] = useState(false);
  const [registrationCode, setRegistrationCode] = useState(incomingCode);
  const [registrationCodeCheck, setRegistrationCodeCheck] =
    useState<RegistrationCodeCheck | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetHandle | null>(null);

  const roleHome = safeLocalRedirect(nextOverride, roleHomeRoute(role));

  useEffect(() => {
    if (role !== 'teacher') return;

    if (incomingMode === 'signup' || incomingCode) {
      setMode('signup');
      setSelectedRole('teacher');
    }

    if (incomingCode) {
      setRegistrationCode(incomingCode);
    }
  }, [incomingCode, incomingMode, role]);

  function requireCaptchaToken() {
    const token = captchaToken || turnstileRef.current?.getToken() || null;
    if (!token) {
      setError('Security check is still loading. Please wait a moment and try again.');
      return null;
    }
    return token;
  }

  function resetCaptcha() {
    setCaptchaToken(null);
    turnstileRef.current?.reset();
  }

  async function syncBootstrapFromClientSession(promoCode?: string) {
    const { data } = await supabase.auth.getSession();
    return syncAulaAccount(data?.session, promoCode);
  }

  async function checkRegistrationCode(raw?: string) {
    const normalized = normalizeRegistrationCode(
      raw === undefined ? registrationCode : raw
    );

    setRegistrationCode(normalized);

    if (!normalized) {
      setRegistrationCodeCheck(null);
      return { ok: true, code: '' };
    }

    try {
      const { data, error } = await supabase.rpc(
        'validate_registration_code',
        { p_code: normalized }
      );

      if (error) {
        setRegistrationCodeCheck({
          ok: false,
          message: 'We could not verify that registration code. Please try again.',
        });
        return { ok: false, code: normalized };
      }

      const result: any = data || {};

      if (!result?.ok) {
        setRegistrationCodeCheck({
          ok: false,
          message: registrationCodeErrorMessage(result?.error),
        });
        return { ok: false, code: normalized };
      }

      const rawPlan = String(result?.grant_plan || '').toLowerCase();
      const planName =
        rawPlan === 'starter'
          ? 'Educator'
          : rawPlan === 'pro'
            ? 'Pro'
            : rawPlan || 'promotional';

      const durationDays = Number(result?.duration_days || 0);
      const durationText =
        durationDays > 0
          ? ` for ${durationDays} day${durationDays === 1 ? '' : 's'}`
          : '';

      const affiliateName = String(result?.affiliate_name || '').trim();
      const affiliateText = affiliateName
        ? ` Courtesy of ${affiliateName}.`
        : '';

      setRegistrationCodeCheck({
        ok: true,
        message: `Code accepted — ${planName} access${durationText}.${affiliateText}`,
      });

      return { ok: true, code: normalized };
    } catch {
      setRegistrationCodeCheck({
        ok: false,
        message: 'We could not verify that registration code. Please try again.',
      });
      return { ok: false, code: normalized };
    }
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const token = requireCaptchaToken();
    if (!token) return;

    setSubmitting(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken: token },
      });
      if (error) {
        setError(error.message);
        return;
      }
      const setup = await syncBootstrapFromClientSession();
      const created = Date.parse(setup.auth_created_at || '');
      const firstLogin = setup.created_personal_org === true || (Number.isFinite(created) && Date.now() - created >= 0 && Date.now() - created < 30 * 60_000);
      router.replace(firstLogin && role === 'teacher' ? '/teacher' : roleHome);
      router.refresh();
    } catch {
      setError('We could not finish setting up your Aula workspace. Please try signing in again.');
    } finally {
      resetCaptcha();
      setSubmitting(false);
    }
  }

  async function handleSignup(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const token = requireCaptchaToken();
    if (!token) return;

    setSubmitting(true);
    try {
      const origin = window.location.origin;
      const next = roleHomeRoute(selectedRole);

      let promoCode = '';
      if (selectedRole === 'teacher') {
        promoCode = normalizeRegistrationCode(registrationCode);

        if (promoCode) {
          const checked = await checkRegistrationCode(promoCode);
          if (!checked.ok) {
            setError('Please correct the registration code or clear the field.');
            return;
          }
          promoCode = checked.code;
        }
      }

      const promoSuffix = promoCode
        ? `&promo=${encodeURIComponent(promoCode)}`
        : '';

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            role: selectedRole,
            ...(promoCode ? { registration_code: promoCode } : {}),
          },
          emailRedirectTo: `${origin}/auth/callback?next=${encodeURIComponent(next)}&role=${encodeURIComponent(selectedRole)}${promoSuffix}`,
          captchaToken: token,
        },
      });

      // Already-registered UX: switch to Sign in instead of confusing verification prompts.
      const msg = (error?.message || '').toLowerCase();
      const alreadyRegistered =
        msg.includes('already registered') ||
        msg.includes('already exists') ||
        msg.includes('user already registered') ||
        (data?.user &&
          Array.isArray(data.user.identities) &&
          data.user.identities.length === 0);

      if (error) {
        if (alreadyRegistered) {
          setMode('signin');
          setInfo('That email is already registered. Please sign in.');
          return;
        }
        setError(error.message);
        return;
      }

      if (data?.session) {
        await syncBootstrapFromClientSession(promoCode);
        router.replace(next);
        router.refresh();
        return;
      }

      setSignupSent(true);
      setInfo(
        'Please check your email to confirm your account. Once confirmed, you’ll be redirected to your dashboard.'
      );
    } catch {
      setError('We could not finish creating your Aula workspace. Please try signing in again.');
    } finally {
      resetCaptcha();
      setSubmitting(false);
    }
  }

  async function handleResendVerification() {
    if (submitting) return;
    setError(null);
    setInfo(null);
    const em = String(email || '').trim();
    if (!em) {
      setError('Enter your email first, then click Resend verification.');
      return;
    }

    const token = requireCaptchaToken();
    if (!token) return;

    setSubmitting(true);
    try {
      const origin = window.location.origin;
      const next = roleHomeRoute(selectedRole);
      const resend = supabase.auth.resend;
      if (typeof resend !== 'function') {
        setInfo('If you did not receive the email, try signing in or use Forgot password.');
        return;
      }
      const promoCode =
        selectedRole === 'teacher'
          ? normalizeRegistrationCode(registrationCode)
          : '';
      const promoSuffix = promoCode
        ? `&promo=${encodeURIComponent(promoCode)}`
        : '';

      const { error } = await resend.call(supabase.auth, {
        type: 'signup',
        email: em,
        options: {
          emailRedirectTo: `${origin}/auth/callback?next=${encodeURIComponent(next)}&role=${encodeURIComponent(selectedRole)}${promoSuffix}`,
          captchaToken: token,
        },
      });
      if (error) {
        setError(error.message);
        return;
      }
      setSignupSent(true);
      setInfo('Verification email sent. Please check your inbox (and spam) for the Aula link.');
    } finally {
      resetCaptcha();
      setSubmitting(false);
    }
  }

  async function handleForgotPassword() {
    if (forgotSubmitting) return;
    setError(null);
    setInfo(null);
    const em = String(email || '').trim();
    if (!em) {
      setError('Enter your email first, then click Forgot password.');
      return;
    }

    const token = requireCaptchaToken();
    if (!token) return;

    setForgotSubmitting(true);
    try {
      const origin = window.location.origin;
      const redirectTo = `${origin}/auth/reset?next=${encodeURIComponent(roleHome)}`;
      const { error } = await supabase.auth.resetPasswordForEmail(em, {
        redirectTo,
        captchaToken: token,
      });
      if (error) {
        setError(error.message);
        return;
      }
      setInfo('Check your email for a password reset link.');
    } finally {
      resetCaptcha();
      setForgotSubmitting(false);
    }
  }

  async function signInWithGoogle() {
    if (googleSubmitting) return;
    setError(null);
    setInfo(null);
    setGoogleSubmitting(true);
    try {
      const roleForGoogle: Role = mode === 'signup' ? selectedRole : role;
      const dest = nextOverride ?? roleHomeRoute(roleForGoogle);

      let promoCode = '';
      if (mode === 'signup' && roleForGoogle === 'teacher') {
        promoCode = normalizeRegistrationCode(registrationCode);

        if (promoCode) {
          const checked = await checkRegistrationCode(promoCode);
          if (!checked.ok) {
            setError('Please correct the registration code or clear the field.');
            return;
          }
          promoCode = checked.code;
        }
      }

      await startGoogleLogin(roleForGoogle, dest, promoCode || undefined);
    } catch (e: any) {
      setError(e?.message || 'Google sign-in failed. Please try again.');
    } finally {
      setGoogleSubmitting(false);
    }
  }

  const title =
    mode === 'signin'
      ? `${role[0].toUpperCase()}${role.slice(1)} Sign In`
      : 'Create your account';

  return (
    <div className="w-full max-w-lg px-4">
      <form
        onSubmit={mode === 'signin' ? handleLogin : handleSignup}
        className="mx-auto rounded-aula-lg border border-aula-border bg-white p-6 shadow-aula-card sm:p-8"
      >
        <h1 className="mb-2 text-center text-3xl font-extrabold tracking-tight text-slate-950">{title}</h1>

        {mode === 'signin' && role === 'teacher' && (
          <p className="mb-8 text-center text-base font-semibold text-emerald-700">
            Create your free teacher account in minutes
          </p>
        )}

        {mode === 'signup' && role === 'teacher' && (
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Account type</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="role"
                  value="teacher"
                  checked={selectedRole === 'teacher'}
                  onChange={() => setSelectedRole('teacher')}
                />
                <span>Teacher</span>
              </label>
            </div>
          </div>
        )}

        {mode === 'signup' && role === 'teacher' && (
          <div className="mb-6">
            <label
              htmlFor="registration-code"
              className="block text-sm font-medium mb-2"
            >
              Registration / promo code
              <span className="ml-1 font-normal text-slate-500">(optional)</span>
            </label>

            <div className="flex gap-2">
              <Input
                id="registration-code"
                name="registration_code"
                type="text"
                autoCapitalize="characters"
                autoComplete="off"
                value={registrationCode}
                onChange={(e) => {
                  setRegistrationCode(
                    normalizeRegistrationCode(e.target.value)
                  );
                  setRegistrationCodeCheck(null);
                  setError(null);
                }}
                onBlur={() => {
                  if (registrationCode) {
                    void checkRegistrationCode();
                  }
                }}
                placeholder="Enter code"
                className="min-w-0 flex-1 uppercase"
              />

              <Button
                type="button"
                variant="secondary"
                disabled={submitting || !registrationCode}
                onClick={() => void checkRegistrationCode()}
              >
                Check
              </Button>
            </div>

            {registrationCodeCheck && (
              <p
                className={`mt-2 text-sm ${
                  registrationCodeCheck.ok
                    ? 'text-emerald-700'
                    : 'text-red-600'
                }`}
              >
                {registrationCodeCheck.message}
              </p>
            )}
          </div>
        )}

        <div className="mb-6">
          <label htmlFor="login-email" className="block text-sm font-medium mb-2">
            Email
          </label>
          <Input
            id="login-email"
            name="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="mb-2">
          <label htmlFor="login-password" className="block text-sm font-medium mb-2">
            Password
          </label>
          {mode === 'signup' && (
            <p className="mt-1 text-xs text-slate-600">
              Use at least <strong>8 characters</strong>. Letters + numbers required; symbols recommended.
            </p>
          )}
          <div className="relative">
            <Input
              id="login-password"
              name="password"
              type={showPw ? 'text' : 'password'}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pr-20"
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-slate-600 hover:text-slate-800"
              aria-label={showPw ? 'Hide password' : 'Show password'}
            >
              {showPw ? '🙈 Hide' : '👁️ Show'}
            </button>
          </div>
        </div>

        {mode === 'signin' && (
          <div className="mb-4 text-right">
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={forgotSubmitting || !captchaToken}
              className="text-sm text-blue-700 hover:underline disabled:opacity-60"
            >
              {forgotSubmitting ? 'Sending…' : 'Forgot password?'}
            </button>
          </div>
        )}

        <TurnstileWidget
          ref={turnstileRef}
          onTokenChange={setCaptchaToken}
          className="mb-3"
        />

        {error ? (
          <Alert tone="danger" className="mb-3">
            {error}
          </Alert>
        ) : null}
        {info ? (
          <Alert tone="success" className="mb-3">
            {info}
          </Alert>
        ) : null}

        <Button
          type="submit"
          size="lg"
          className="w-full"
          loading={submitting}
          disabled={
            submitting ||
            !captchaToken ||
            (mode === 'signup' && signupSent)
          }
        >
          {submitting
            ? mode === 'signin'
              ? 'Signing in…'
              : 'Creating account…'
            : mode === 'signin'
              ? 'Sign In'
              : 'Create Account'}
        </Button>

        <div className="mt-4 text-center text-sm">
          {mode === 'signin' ? (
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="w-full"
              onClick={() => {
                setError(null);
                setInfo(null);
                setMode('signup');
                setSelectedRole(role);
              }}
            >
              Create your free Aula account
            </Button>
          ) : (
            <button
              type="button"
              className="text-blue-700 hover:underline"
              onClick={() => {
                setError(null);
                setInfo(null);
                setMode('signin');
              }}
            >
              Already have an account? Sign in
            </button>
          )}
        </div>

        {mode === 'signin' && role === 'teacher' && (
          <p className="mt-2 text-center text-xs text-slate-500">
            No credit card required.
          </p>
        )}

        {/* AULA_GOOGLE_LOGIN_STUDENT_V2 */}
        {role === 'teacher' && (
          <div className="mt-6">
            {mode === 'signup' && signupSent && (
              <div className="mb-6">
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  className="w-full"
                  onClick={handleResendVerification}
                  disabled={submitting || !captchaToken}
                >
                  Resend verification email
                </Button>
              </div>
            )}

            <div className="flex items-center my-6">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="px-3 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">or use Google</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <div className="rounded-aula-lg border-2 border-indigo-200 bg-indigo-50/70 p-4 shadow-sm">
              <div className="mb-3 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-lg font-extrabold text-blue-600 shadow-sm ring-1 ring-slate-200" aria-hidden="true">G</div>
                <div className="min-w-0 text-left">
                  <p className="font-bold text-slate-950">Google sign in</p>
                  <p className="text-xs leading-5 text-slate-600">Fastest way to sign in or create your free teacher account.</p>
                </div>
              </div>

              <Button
                type="button"
                size="lg"
                className="w-full"
                onClick={signInWithGoogle}
                loading={googleSubmitting}
                disabled={googleSubmitting}
              >
                {googleSubmitting ? 'Connecting…' : 'Continue free with Google'}
              </Button>

              {mode === 'signin' && (
                <p className="mt-3 text-center text-xs font-medium text-slate-600">
                  No credit card required.
                </p>
              )}
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
